import { ACCOUNTING_PERIOD_STATES, type AccountingPeriodListQuery, type AccountingPeriodView } from '@chukua/common';
import { Button, Tag, type TableColumnsType } from 'antd';
import type { ListQueryControls } from '../../../shared/hooks/use-list-query';
import {
  availablePeriodAction,
  PERIOD_ACTION_LABELS,
  PERIOD_STATE_COLORS,
  PERIOD_STATE_LABELS,
  type PeriodActionKind,
  type PeriodPermissions,
} from '../policies/period-actions.policy';

interface BuildPeriodColumnsArgs {
  list: Pick<ListQueryControls<AccountingPeriodListQuery>, 'sortOrderFor' | 'filteredValueFor'>;
  permissions: PeriodPermissions;
  busyId: string | null;
  onAct: (period: AccountingPeriodView, action: PeriodActionKind) => void;
}

const formatDate = (isoDate: string) => new Date(`${isoDate}T00:00:00Z`).toLocaleDateString(undefined, { timeZone: 'UTC', dateStyle: 'medium' });

/** Sorting and filtering run on the server: each column's `key` is its `sort` field or filter param in `accountingPeriodListQuerySchema`. */
export function buildPeriodColumns({ list, permissions, busyId, onAct }: BuildPeriodColumnsArgs): TableColumnsType<AccountingPeriodView> {
  return [
    { title: 'From', key: 'startDate', dataIndex: 'startDate', sorter: true, sortOrder: list.sortOrderFor('startDate'), render: formatDate },
    { title: 'To', key: 'endDate', dataIndex: 'endDate', sorter: true, sortOrder: list.sortOrderFor('endDate'), render: formatDate },
    {
      title: 'State',
      key: 'state',
      filters: ACCOUNTING_PERIOD_STATES.map((state) => ({ text: PERIOD_STATE_LABELS[state], value: state })),
      filteredValue: list.filteredValueFor('state'),
      sorter: true,
      sortOrder: list.sortOrderFor('state'),
      render: (_, period) => <Tag color={PERIOD_STATE_COLORS[period.state]}>{PERIOD_STATE_LABELS[period.state]}</Tag>,
    },
    {
      title: 'Locked',
      key: 'lockedAt',
      render: (_, period) => (period.lockedAt ? new Date(period.lockedAt).toLocaleString() : '—'),
    },
    {
      title: '',
      key: 'actions',
      align: 'right',
      render: (_, period) => {
        const action = availablePeriodAction(period.state, permissions);
        if (!action) return null;
        return (
          <Button size="small" loading={busyId === period.id} onClick={() => onAct(period, action)}>
            {PERIOD_ACTION_LABELS[action]}
          </Button>
        );
      },
    },
  ];
}
